import { Component, Input, OnInit } from '@angular/core';
import { MedicareClaimStatus } from '../../../../models/api/medicare/medicare-claim-status.model';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { BaseComponent } from '../../../base/base.component';
import { HicnLabelComponent } from '../../../core/labels/hicn-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-medicare',
  imports: [
    PageHeaderComponent,
    HicnLabelComponent,
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
  ],
  template: `<app-page-header label="Medicare" />
    @if (dataSource) {
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-1/3">
          <div class="tw-flex tw-flex-col tw-gap-8">
            <app-card label="Beneficiary Status">
              <app-detail-panel [dataSource]="dataSource.beneficiaryStatus">
                <app-detail-item
                  label="Medicare Beneficiary"
                  name="beneficiary"
                />
                <app-detail-item label="HICN" name="hicn" labelCase="none">
                  <ng-template #value>
                    <app-hicn-label
                      [value]="dataSource.beneficiaryStatus.hicn"
                    />
                  </ng-template>
                </app-detail-item>
              </app-detail-panel>
            </app-card>
            <app-card
              label="Ongoing responsibility for medicals ORM"
              labelCase="none"
            >
              <app-detail-panel [dataSource]="dataSource.ormInfo">
                <app-detail-item
                  label="ORM accepted"
                  name="accepted"
                  format="b"
                  labelCase="none"
                />
                <app-detail-item
                  label="ORM terminated"
                  name="terminated"
                  format="b"
                  labelCase="none"
                />
                <app-detail-item
                  label="ORM termination date"
                  name="termDate"
                  format="d"
                  labelCase="none"
                />
              </app-detail-panel>
            </app-card>
          </div>
        </div>
        <div class="tw-basis-1/3">
          <app-card label="Reporting Dates">
            <app-detail-panel [dataSource]="dataSource.reportingInfo">
              <app-detail-item
                label="Claim Reported"
                name="claimReported"
                format="b"
              />
              <app-detail-item
                label="Date First Reported"
                name="firstReported"
                format="d"
              />
              <app-detail-item
                label="Date Last Reported"
                name="lastReported"
                format="d"
              />
              <app-detail-item
                label="Next Reported Date"
                name="nextReporting"
                format="d"
              />
              <app-detail-item
                label="CMS status"
                name="status"
                labelCase="none"
              />
            </app-detail-panel>
          </app-card>
        </div>
        <div class="tw-basis-1/3">
          <app-card label="Responsible reporting entity RRE" labelCase="none">
            <app-table
              [dataSource]="dataSource.rreItems"
              [showPaginator]="false"
              [showSort]="false"
              [showFilter]="false"
              [showFilterButton]="false"
              [showRecordLabel]="false"
            >
              <app-column name="id" label="ID" width="20%" />
              <app-column name="name" label="Name" width="80%" />
            </app-table>
          </app-card>
        </div>
      </div>
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-2/3">
          <app-card
            label="ICD diagnosis for Medicare reporting"
            labelCase="none"
          >
            <app-table
              [dataSource]="dataSource.icdItems"
              [pageSize]="5"
              recordLabel="ICD Diagnosis"
              recordLabelPlural="ICD diagnosis"
              recordLabelCase="none"
            >
              <app-column name="id" label="ICD ID" width="20%" />
              <app-column name="version" label="Ver" width="20%" format="n" />
              <app-column name="code" label="Code" width="20%" />
              <app-column name="description" label="Description" width="40%" />
            </app-table>
          </app-card>
        </div>
      </div>
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-2/3">
          <app-card label="Total Payments of Obligation To Claimant">
            <app-table
              [dataSource]="dataSource.tpocItems"
              [pageSize]="5"
              recordLabel="payment"
            >
              <app-column name="id" label="TPOC ID" width="30%" />
              <app-column
                name="date"
                label="TPOC Date"
                width="30%"
                format="d"
              />
              <app-column
                name="amount"
                label="TPOC Amount"
                width="40%"
                format="c"
              />
            </app-table>
          </app-card>
        </div>
      </div>
    }`,
  styles: [],
})
export class MedicareComponent extends BaseComponent implements OnInit {
  @Input() dataSource: MedicareClaimStatus;

  async ngOnInit(): Promise<void> {
    // If dataSource is not provided (when used as a routed component), load it
    if (!this.dataSource) {
      await this.loadMedicareData();
    }
    this.view.hideLoading();
  }

  private async loadMedicareData(): Promise<void> {
    const claim: ClaimRow = this.view.currentClaim();

    if (!claim) {
      return;
    }

    this.dataSource = await this.api.medicare.getClaimStatus(
      claim.recordSource,
      claim.recordId,
      claim.contractNumber,
    );
  }
}
